import React, { useEffect, useState } from 'react';
import { Alert, Button, Content, Modal, ModalBody, ModalFooter, ModalHeader } from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { ApiResponseAgentDisable } from '../../../types/api/ApiResponseAgent';

interface DisableAgentModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** The tenant's agent Keycloak client id, for the confirmation prose. */
  clientId: string | null;
  /** Called after a successful disable so the parent can clear held credentials and refetch. */
  onDisabled: () => Promise<void> | void;
}

/**
 * Confirm-disable modal for the tenant agent feature. DELETE /organization/agent removes the
 * dedicated Keycloak client, so any deployed agent instantly loses authentication — hence the
 * explicit confirmation. Failures are JSend `error` at HTTP 200, so success is decided by
 * `body.status === 'success'` + the `agent` data key, not `response.ok` alone.
 */
export const DisableAgentModal: React.FC<DisableAgentModalProps> = ({ isOpen, onClose, clientId, onDisabled }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }
    onClose();
  };

  const handleDisable = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await apiFetch('/organization/agent', { method: 'DELETE' });

      let body: ApiResponseAgentDisable | null;
      try {
        body = (await response.json()) as ApiResponseAgentDisable;
      } catch {
        body = null;
      }

      if (response.ok && body?.status === 'success' && body.data?.agent) {
        addNotification({
          title: 'Agent disabled',
          srTitle: 'Success alert',
          description: 'The agent feature was disabled and its credentials were revoked.',
          variant: 'success',
        });
        setDrawerOpen(true);
        await onDisabled();
        onClose();
        return;
      }
      setError(body?.message ?? `Request failed with status ${response.status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected network error occurred.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal variant="small" isOpen={isOpen} onClose={handleClose} onEscapePress={handleClose} aria-label="Disable agent">
      <ModalHeader title="Disable agent?" titleIconVariant="warning" />
      <ModalBody>
        {error && (
          <Alert variant="danger" isInline title="Disable failed" className="pf-v6-u-mb-md">
            {error}
          </Alert>
        )}
        <Content>
          Disable the agent feature for your organization?{' '}
          {clientId ? (
            <>
              The dedicated Keycloak client <strong>{clientId}</strong> will be deleted
            </>
          ) : (
            'Its dedicated Keycloak client will be deleted'
          )}{' '}
          and every deployed agent immediately stops authenticating. To use agents again, enable the feature and
          reconfigure them with the new credentials.
        </Content>
      </ModalBody>
      <ModalFooter>
        <Button variant="danger" onClick={() => void handleDisable()} isLoading={isSubmitting} isDisabled={isSubmitting}>
          Disable
        </Button>
        <Button variant="link" onClick={handleClose} isDisabled={isSubmitting}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
};
